import { useMutation } from 'react-query';
import { toast } from 'sonner';

import { CalendarReminder } from '../../../../backend/src/matter/procedural-timeline/calendar-invite.builder';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface DownloadCalendarVars {
  /** Omit to download every dated event as a zip. */
  eventId?: string;
  reminder: CalendarReminder;
  filename: string;
}

/**
 * Downloads a procedural-timeline event (or all of them, as a zip) as a
 * calendar file. Fetches the file with auth, then triggers a browser save.
 */
export const useDownloadProceduralCalendar = (matterId: string | undefined) => {
  const { getFetchConfig } = useGetFetchConfig();

  return useMutation<void, Error, DownloadCalendarVars>(
    async ({ eventId, reminder, filename }) => {
      if (matterId === undefined) throw new Error('Missing matter');

      const path = eventId === undefined ? 'events/calendar-download' : `events/${eventId}/calendar-download`;
      const res = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/procedural-timeline/${path}`,
        getFetchConfig({ method: FetchMethod.POST, data: { reminder } }),
      );
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(detail || 'Failed to download calendar file');
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    },
    {
      onSuccess: (_data, { eventId, reminder }) => {
        trackCustomEvent(TrackingEventsV2.ProceduralTimelineCalendarDownload, {
          scope: eventId === undefined ? 'all' : 'event',
          reminder,
        });
      },
      onError: () => {
        toast.error('Failed to download calendar file. Please try again.');
      },
    },
  );
};
